<script lang="ts">
  /**
   * ClubSessionHeader — the ‹ morning › date nav for the Club, reusing the
   * DateNavHeader chrome (Task 3) wired to the SESSION nav model (Plan 2) instead
   * of the my-workout model. With `onOpenCalendar` the date is the same calendar
   * button as the Swim tab's (ClubTab opens the Club variant of that sheet).
   */
  import DateNavHeader from "./DateNavHeader.svelte";
  import type { ClubSession } from "../lib/clubSessions";
  import { sessionNavState, currentSession } from "../lib/clubSessionNav";
  import { selectedSessionId } from "../lib/state";
  import { formatWorkoutDay, formatWorkoutDayShort } from "../lib/format";
  import { haptic } from "../lib/haptics";

  interface Props {
    sessions: ClubSession[];
    onOpenCalendar?: (() => void) | null;
    /** Whether the row shows the short label (B-031: the card under it follows). */
    onFit?: ((abbreviated: boolean) => void) | null;
  }
  let { sessions, onOpenCalendar = null, onFit = null }: Props = $props();

  let shown = $derived(currentSession(sessions, $selectedSessionId));
  // SharedMorningView only renders this header once sessions.length > 0, so
  // `shown` never resolves to null here in practice — this is an honest
  // fallback, not a reachable zero-state (VP round 2, blocker 3: plain words,
  // no jargon, in case a future caller ever hits it with zero sessions).
  let dateText = $derived(shown ? formatWorkoutDay(shown.startDate) : "No swims yet");
  let nav = $derived(sessionNavState(sessions, $selectedSessionId));

  // Gate on canPrev/canNext, NOT prev/next === null — the index-1 "next" sentinel
  // is null while canNext is true (Plan-2 carry-forward). Setting null selects the
  // most-recent session, exactly like selectedWorkoutUUID on the Swim tab.
  function goPrev() {
    if (!nav.canPrev) return;
    haptic("selection");
    selectedSessionId.set(nav.prev);
  }
  function goNext() {
    if (!nav.canNext) return;
    haptic("selection");
    selectedSessionId.set(nav.next);
  }
</script>

<DateNavHeader
  {dateText}
  shortDateText={shown ? formatWorkoutDayShort(shown.startDate) : null}
  canPrev={nav.canPrev}
  canNext={nav.canNext}
  onPrev={goPrev}
  onNext={goNext}
  prevLabel="Previous morning, older"
  nextLabel="Next morning, newer"
  {onOpenCalendar}
  {onFit}
  calendarLabel="Open the club calendar"
/>
